/**
 * OpsKpiStrip — Premium soft-tinted stat cards for workspace KPIs.
 *
 * Each card uses a semantic color tint (blue, green, purple, orange, red).
 * Supports icon, label, main value, sub-value, and optional trend.
 *
 * Usage:
 *   <OpsKpiStrip items={[
 *     { label: 'Total Assistance', value: '$12,500', variant: 'blue', icon: DollarSign },
 *     { label: 'Utilized', value: '$8,200', sub: '65.6%', variant: 'green', icon: TrendingUp },
 *   ]} />
 */
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { wsStatVariants, type WsStatVariant } from '@/design-system/tokens/workspaceTokens';

export interface OpsKpiItem {
  label: string;
  value: string | number;
  sub?: string;
  icon?: LucideIcon;
  variant?: WsStatVariant;
  active?: boolean;
  onClick?: () => void;
}

interface OpsKpiStripProps {
  items: OpsKpiItem[];
  isLoading?: boolean;
  skeletonCount?: number;
  className?: string;
}


function KpiSkeleton() {
  return (
    <div className="ops-stat ops-stat--blue animate-pulse">
      <div className="h-3 w-20 rounded bg-muted mb-2" />
      <div className="h-7 w-14 rounded bg-muted" />
    </div>
  );
}

export function OpsKpiStrip({ items, isLoading, skeletonCount, className }: OpsKpiStripProps) {
  if (isLoading) {
    return (
      <div className={cn('grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3', className)}>
        {Array.from({ length: skeletonCount ?? 5 }).map((_, i) => <KpiSkeleton key={i} />)}
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3', className)}>
      {items.map((item) => {
        const variant = item.variant ?? 'blue';
        const Icon = item.icon;
        const Component = item.onClick ? 'button' : 'div';

        return (
          <Component
            key={item.label}
            type={item.onClick ? 'button' : undefined}
            onClick={item.onClick}
            className={cn(
              wsStatVariants[variant],
              'text-left',
              item.active && 'ring-2 ring-offset-1 ring-current/40',
              item.onClick && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="ops-stat__label">{item.label}</div>
                <div className="ops-stat__value">{item.value}</div>
                {item.sub && <div className="ops-stat__sub">{item.sub}</div>}
              </div>
              {Icon && (
                <div className="flex items-center justify-center w-9 h-9 rounded-[14px] flex-shrink-0 bg-white/20">
                  <Icon className="w-[18px] h-[18px] text-white" />
                </div>
              )}
            </div>
          </Component>
        );
      })}
    </div>
  );
}

export default OpsKpiStrip;
